import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { MeetingTemplateSection } from '@wundo/shared/meetingTemplate.ts';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import { SortableTemplateTopic } from './sortable-template-topic.tsx';

/* A topic with the key it keeps while it moves; topics have no id of their own. */
export interface KeyedTopic extends MeetingTemplateSection {
  key: number;
}

export interface TemplateTopicsProps {
  topics: KeyedTopic[];
  labels: MeetingTemplateEditorLabels;
  onChange: (topics: KeyedTopic[]) => void;
}

/**
 * The ordered topics of a template. The order is part of what the template
 * says: every conversation of this type walks through them top to bottom.
 */
export function TemplateTopics({ topics, labels, onChange }: TemplateTopicsProps) {
  /* A few pixels of slack, or a plain click on the grip already starts a drag. */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  /* What the last keyboard move did, for a reader who cannot see it happen. */
  const [moved, setMoved] = useState('');

  const idOf = (topic: KeyedTopic) => `topic-${topic.key}`;
  const placeOf = (id: UniqueIdentifier) =>
    topics.findIndex((topic) => idOf(topic) === String(id)) + 1;

  function change(index: number, patch: Partial<MeetingTemplateSection>) {
    onChange(topics.map((topic, at) => (at === index ? { ...topic, ...patch } : topic)));
  }

  function moveBy(index: number, direction: -1 | 1) {
    const to = index + direction;
    if (to < 0 || to >= topics.length) return;
    setMoved(labels.topicDragOver({ position: to + 1, total: topics.length }));
    onChange(arrayMove(topics, index, to));
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (over === null || active.id === over.id) return;
    const from = placeOf(active.id) - 1;
    const to = placeOf(over.id) - 1;
    if (from === -1 || to === -1) return;
    onChange(arrayMove(topics, from, to));
  }

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: labels.topicDragInstructions,
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => labels.topicDragStart({ position: placeOf(active.id) }),
    onDragOver: ({ active, over }) =>
      over && over.id !== active.id
        ? labels.topicDragOver({ position: placeOf(over.id), total: topics.length })
        : undefined,
    onDragEnd: ({ over }) =>
      over
        ? labels.topicDragEnd({ position: placeOf(over.id), total: topics.length })
        : labels.topicDragCancel,
    onDragCancel: () => labels.topicDragCancel,
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements, screenReaderInstructions }}
    >
      <SortableContext items={topics.map(idOf)} strategy={verticalListSortingStrategy}>
        {topics.map((topic, index) => (
          <SortableTemplateTopic
            key={topic.key}
            sortableId={idOf(topic)}
            position={index + 1}
            topic={topic}
            labels={labels}
            removable={topics.length > 1}
            onChange={(patch) => change(index, patch)}
            onRemove={() => onChange(topics.filter((_, at) => at !== index))}
            onMove={(direction) => moveBy(index, direction)}
          />
        ))}
      </SortableContext>

      {/* dnd-kit never announces a keyboard move: it is over when it is made. */}
      <p aria-live='polite' className='sr-only'>
        {moved}
      </p>
    </DndContext>
  );
}
